/**
 * WeaveIn.ai Platform - Public Form Share Dialog
 * Generate public links, QR codes, and embed codes for forms.
 */

import React, { useState, useEffect } from 'react';
import {
  X, Link, QrCode, Code, Copy, CheckCircle, Loader2, ExternalLink,
  Plus, Trash2, Eye, Globe
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';
import PublicLinkQRCode from './PublicLinkQRCode';

interface PublicFormShareDialogProps {
  templateId: number;
  templateName: string;
  programId?: number;
  onClose: () => void;
}

interface PublicToken {
  id: number;
  token: string;
  title?: string;
  is_active: boolean;
  expires_at?: string;
  max_submissions?: number;
  current_submissions: number;
  public_url: string;
  created_at?: string;
  anonymous_responses?: boolean;
  public_results_enabled?: boolean;
  results_url?: string | null;
}

export default function PublicFormShareDialog({ templateId, templateName, programId, onClose }: PublicFormShareDialogProps) {
  // `t` is taken by the token loop variable below, hence `tr`.
  const { t: tr } = useAppTranslation();
  const [tokens, setTokens] = useState<PublicToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // New token settings
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [maxSubmissions, setMaxSubmissions] = useState<number | undefined>();
  const [createContact, setCreateContact] = useState(true);
  // TRUS-624: an anonymous survey stores no name/email/phone and splits its
  // optional contact section off; live results are aggregate totals only.
  const [anonymous, setAnonymous] = useState(false);
  const [publicResults, setPublicResults] = useState(false);
  const [successMessage, setSuccessMessage] = useState('Thank you for your submission.');

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const loadTokens = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/forms/public-tokens');
      const allTokens = Array.isArray(res.data) ? res.data : [];
      setTokens(allTokens.filter((t: any) => t.form_template_id === templateId));
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadTokens(); }, [templateId]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await apiClient.post('/api/forms/public-tokens', {
        form_template_id: templateId,
        program_id: programId,
        title: newTitle || templateName,
        max_submissions: maxSubmissions,
        create_contact_on_submit: createContact && !anonymous,
        anonymous_responses: anonymous,
        public_results_enabled: publicResults,
        success_message: successMessage,
      });
      setShowCreateForm(false);
      setNewTitle('');
      await loadTokens();
    } catch {}
    finally { setCreating(false); }
  };

  const handleDeactivate = async (tokenId: number) => {
    try {
      await apiClient.delete(`/api/forms/public-tokens/${tokenId}`);
      await loadTokens();
    } catch {}
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const getFullUrl = (token: string) => `${baseUrl}/public/forms/${token}`;
  const getEmbedCode = (token: string) => `<iframe src="${getFullUrl(token)}" width="100%" height="800" frameborder="0"></iframe>`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Share Form Publicly</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-5 h-5 text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-500">
            Generate public links for <strong>{templateName}</strong>. Shared forms can be accessed without login via URL, QR code, or embedded on external websites.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-3 text-xs text-blue-800 dark:text-blue-200">
            <strong>Anyone with the link can submit this form</strong> — no account, login, or existing member record required. With auto-create enabled (default), each submission becomes a new contact in your organization that staff can promote to a member later.
          </div>

          {/* Existing tokens */}
          {loading ? (
            <div className="flex items-center gap-2 py-4 justify-center text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /></div>
          ) : tokens.length > 0 ? (
            <div className="space-y-3">
              {tokens.map((t) => (
                <div key={t.id} className={`p-3 border rounded-lg ${t.is_active ? 'border-gray-200 dark:border-gray-700' : 'border-gray-100 opacity-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.title || 'Public Link'}</span>
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${t.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">{t.current_submissions} submission{t.current_submissions !== 1 ? 's' : ''}</span>
                  </div>

                  {t.is_active && (
                    <div className="space-y-2">
                      {/* URL */}
                      <div className="flex items-center gap-1.5">
                        <Link className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <input readOnly value={getFullUrl(t.token)} className="flex-1 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-600" />
                        <button onClick={() => copyToClipboard(getFullUrl(t.token), `url-${t.id}`)} className="p-1 hover:bg-gray-100 rounded">
                          {copied === `url-${t.id}` ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
                        </button>
                      </div>

                      {/* Embed */}
                      <div className="flex items-center gap-1.5">
                        <Code className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <input readOnly value={getEmbedCode(t.token)} className="flex-1 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-600" />
                        <button onClick={() => copyToClipboard(getEmbedCode(t.token), `embed-${t.id}`)} className="p-1 hover:bg-gray-100 rounded">
                          {copied === `embed-${t.id}` ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
                        </button>
                      </div>

                      <div className="flex gap-2 mt-1">
                        <a href={getFullUrl(t.token)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                          <ExternalLink className="w-3 h-3" /> Preview
                        </a>
                        {t.results_url && (
                          <a href={`${window.location.origin}${t.results_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            <ExternalLink className="w-3 h-3" /> {tr('public_form_share.live_results', 'Live results')}
                          </a>
                        )}
                        <button onClick={() => handleDeactivate(t.id)} className="flex items-center gap-1 text-xs text-red-500 hover:underline">
                          <Trash2 className="w-3 h-3" /> Deactivate
                        </button>
                      </div>

                      {/* QR code: generate, download, share, copy, print */}
                      <PublicLinkQRCode linkId={t.id} title={t.title || templateName} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {/* Create new token */}
          {!showCreateForm ? (
            <button onClick={() => setShowCreateForm(true)} className="flex items-center gap-1.5 w-full px-4 py-3 text-sm font-medium border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600">
              <Plus className="w-4 h-4" /> Generate New Public Link
            </button>
          ) : (
            <div className="p-4 border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg space-y-3">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">New Public Link</h4>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder={templateName} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Submissions (optional)</label>
                <input type="number" value={maxSubmissions ?? ''} onChange={(e) => setMaxSubmissions(e.target.value ? parseInt(e.target.value) : undefined)} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" placeholder="Unlimited" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Success Message</label>
                <input value={successMessage} onChange={(e) => setSuccessMessage(e.target.value)} className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100" />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="rounded border-gray-300 mt-0.5" />
                <span>
                  <span className="font-medium">{tr('public_form_share.anonymous', 'Anonymous responses')}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {tr(
                      'public_form_share.anonymous_help',
                      'No name, email or phone is asked for or kept. Fields marked as contact details become a separate contact, with no link to the answers. Cannot be changed later.',
                    )}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={publicResults} onChange={(e) => setPublicResults(e.target.checked)} className="rounded border-gray-300 mt-0.5" />
                <span>
                  <span className="font-medium">{tr('public_form_share.public_results', 'Show live results to anyone with the results link')}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {tr(
                      'public_form_share.public_results_help',
                      'Totals for multiple-choice questions only, shown once 5 people have answered. Written answers are never published.',
                    )}
                  </span>
                </span>
              </label>
              {!anonymous && (
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={createContact} onChange={(e) => setCreateContact(e.target.checked)} className="rounded border-gray-300 mt-0.5" />
                <span>
                  <span className="font-medium">Auto-create contact on submission</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Recommended. Each submission becomes a contact record. Uncheck only if you want submissions stored without creating any contact (e.g. anonymous surveys).
                  </span>
                </span>
              </label>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowCreateForm(false)} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreate} disabled={creating} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />} Generate
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
